import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  AppIconTile,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  bkdp,
  FolderOutlinedIcon,
  HomeOutlinedIcon,
  MenuIcon,
  MenuOpenIcon,
  PersonIcon,
  SettingsIcon
} from "@app/ui-kit";
import { useAuth } from "@app/auth-client";
import { APP_SLUGS, usePortal } from "@app/portal-sdk";
import { APP_REGISTRY } from "../apps/registry";
import { settingsPath, settingsSections } from "../client/settings/sections";
import { useShell } from "./ShellContext";

const drawerWidth = 240;

// NAV-3: icon and iconColor are optional so the admin branch compiles unchanged; AppIconTile supplies the neutral
// mark when either is missing.
type NavItem = { label: string; path: string; exact?: boolean; icon?: React.ElementType; iconColor?: string };
/** MNT-2: the navigation is sections; a client portal has one unlabelled section, the admin console two labelled ones. */
type NavSection = { label?: string; items: NavItem[] };

type AppShellProps = {
  children: React.ReactNode;
};

/**
 * Persistent frame: BKDP logo, client switcher, contextual left nav, user menu.
 * Navigation depends on where the user is: the admin console or a client portal.
 */
export const AppShell = ({ children }: AppShellProps) => {
  const isMobile = useMediaQuery("(max-width:900px)");
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [userAnchorEl, setUserAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { me, isBkdpAdmin, memberships } = usePortal();
  const shell = useShell();

  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const inAdmin = location.pathname.startsWith("/admin");
  const client = shell.client;
  const inClient = Boolean(client) && location.pathname.startsWith(`/c/${client?.slug}`);

  const navSections = useMemo<NavSection[]>(() => {
    if (inAdmin) {
      // MNT-2: two sections, and the line between them decides every placement. Clients is per client: the register,
      // and the client that is open named beneath it. Applications is per application with no client at all: one row
      // per application whose registry declaration says BKDP maintains something for it, with its own mark, in the
      // order the client navigation orders applications. An application with nothing to maintain is not a row.
      const clients: NavItem[] = [{ label: "All clients", path: "/admin", exact: true }];
      if (shell.adminClient && location.pathname.startsWith(`/admin/clients/${shell.adminClient.id}`)) clients.push({ label: shell.adminClient.name, path: `/admin/clients/${shell.adminClient.id}` });
      const apps: NavItem[] = APP_SLUGS.map((slug) => APP_REGISTRY[slug]).filter((def) => def?.maintained).map((def) => ({ label: def.name, path: `/admin/apps/${def.slug}`, icon: def.icon, iconColor: def.iconColor }));
      return [{ label: "Clients", items: clients }, { label: "Applications", items: apps }];
    }
    if (inClient && client) {
      const base = `/c/${client.slug}`;
      // SET-2: the left nav is places the work happens: Home, Documents, one entry per enabled application.
      // Team and Reference data configure the client and live behind the gear (SET-1).
      // NAV-3 decision 4: every entry gets a mark. Home and Documents are core features with no registry row, so
      // they take the neutral slate; each application carries def.icon / def.iconColor verbatim (decision 3).
      const items: NavItem[] = [{ label: "Home", path: base, exact: true, icon: HomeOutlinedIcon, iconColor: bkdp.slate }];
      if (client.features.documents !== false) {
        items.push({ label: "Documents", path: `${base}/documents`, icon: FolderOutlinedIcon, iconColor: bkdp.slate });
      }
      for (const slug of client.enabledApps) {
        const def = APP_REGISTRY[slug];
        if (def) items.push({ label: def.navLabel ?? def.name, path: `${base}/apps/${slug}`, icon: def.icon, iconColor: def.iconColor });
      }
      return [{ items }];
    }
    return [];
  }, [inAdmin, inClient, client, shell.canManage, shell.adminClient, location.pathname]);
  const navItems = useMemo(() => navSections.flatMap((s) => s.items), [navSections]);

  const isSelected = (item: NavItem) =>
    item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  // SET-1: the gear is client settings, a place, shown only inside a client portal and only when the person can
  // reach at least one section there. The person icon stays the account.
  const settingsHome = inClient && client ? settingsPath(client.slug) : null;
  const showGear = Boolean(settingsHome) && settingsSections({ canManage: shell.canManage }).length > 0;
  const inSettings = Boolean(settingsHome) && location.pathname.startsWith(settingsHome!);


  const activeMemberships = memberships.filter((m) => m.status === "active");
  // HDR-3: the account menu answers "who am I and where am I". Switching client is context, so it lives here;
  // the gear stays this client's settings. The common case, one membership and no admin rights, is the account
  // block and Sign out and nothing else.
  const showAdminConsole = isBkdpAdmin && !inAdmin;
  const showSwitch = activeMemberships.length > 1 || (isBkdpAdmin && activeMemberships.length > 0);
  const showAllClients = isBkdpAdmin;
  const menuHasClients = showSwitch || showAllClients;
  const accountLabel = menuHasClients ? "Account and clients" : "Account";
  // TOP-2: the safety signal. A BKDP admin inside someone else's portal, and nowhere else.
  const viewingAsBkdp = isBkdpAdmin && inClient && client !== null;
  const viewingSentence = client ? `Viewing ${client.name} as BKDP` : "";

  const roleLabel = (() => {
    if (viewingAsBkdp) return viewingSentence;
    if (isBkdpAdmin) return "BKDP admin";
    if (client) {
      const membership = memberships.find((m) => m.client.id === client.id);
      if (membership) return membership.role === "client_admin" ? "Client admin" : "Member";
    }
    return null;
  })();

  const handleLogout = async () => {
    setUserAnchorEl(null);
    await logout();
  };

  const goTo = (path: string) => {
    navigate(path);
    setUserAnchorEl(null);
    if (isMobile) setDrawerOpen(false);
  };

  const drawerContent = (
    <Box sx={{ width: drawerWidth, display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar />
      {navItems.length > 0 && (
        <>
          {/* ACT-3: the client signs the top of their own navigation: the logo when they have one, with their name
              as its alt, otherwise their name as the overline. Never both. The admin console keeps its eyebrow. */}
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            {!inAdmin && client?.logoUrl ? (
              <Box component="img" src={client.logoUrl} alt={client.name} sx={{ display: "block", maxWidth: 192, maxHeight: 40, width: "auto", height: "auto" }} />
            ) : (
              <Typography variant="overline" sx={{ color: "secondary.main" }}>
                {inAdmin ? "Admin Console" : client?.name ?? "Navigation"}
              </Typography>
            )}
          </Box>
          {navSections.map((section, i) => (
            <Box key={section.label ?? "nav"} sx={{ mt: i > 0 ? 2 : 0 }} data-testid={section.label ? `nav-section-${section.label.toLowerCase()}` : "nav-section"}>
              {/* MNT-2 criterion 1: the section label is overline text in the portal's own style, not a row. */}
              {section.label && <Typography variant="overline" sx={{ color: "text.secondary", display: "block", px: 3, mb: 0.5 }} data-nav-section={section.label}>{section.label}</Typography>}
              <List sx={{ px: 1, py: 0 }}>
                {section.items.map((item) => (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton selected={isSelected(item)} onClick={() => goTo(item.path)} data-nav-item={item.label}>
                      {/* NAV-3: the gutter is gated on the branch, not on the fields. In a client portal every row has a
                          mark, and an application with no icon gets the tile's neutral fallback rather than a blank
                          gutter (c12). MNT-2: in the admin console the Clients rows stay text (c10) and an application
                          row carries its own mark, so it reads as the same application the client navigation shows.
                          32 px: a 24 px mark and 8 px of air. */}
                      {(!inAdmin || item.icon) && (
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <AppIconTile icon={item.icon} color={item.iconColor} size="nav" />
                        </ListItemIcon>
                      )}
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </>
      )}
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ px: 3, py: 3, borderTop: "1.5px solid", borderColor: "divider" }}>
        <Typography variant="overline" sx={{ color: "secondary.main", display: "block", mb: 0.5 }}>
          BKDP
        </Typography>
        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          The hard thinking has already been done.
        </Typography>
      </Box>
    </Box>
  );

  const hasNav = navItems.length > 0;
  // PHN (finding 9): above 900px the navigation is the persistent drawer. Below it the same drawer opens as an overlay
  // from the same toolbar button and closes on navigation; nine destinations do not fit a bottom bar, and a strip that
  // scrolls hides what is past its edge, which was the defect.
  const showDrawer = !isMobile && hasNav;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="fixed" sx={{ zIndex: 1201 }}>
        {/* TOP-1: three zones. Zone 1 is the frame: the drawer toggle and the BKDP logo, at the window's left gutter
            with the drawer's own padding, outside the content measure and never sliding with it. Zones 2 and 3 take
            the measure main uses (maxWidth 1200, the same gutters), offset by the persistent drawer when it is open,
            so the client's brand mark starts on the content's left edge and the person icon ends on its right edge
            (HDR-2). With the drawer collapsed the measure spans the window like main does, and its left gutter
            clears zone 1 where the content's left edge would fall under it. */}
        <Toolbar disableGutters sx={{ px: 0, position: "relative" }}>
          <Box
            sx={{
              position: { xs: "static", md: "absolute" },
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: 1,
              width: showDrawer ? drawerWidth : "auto",
              boxSizing: "border-box",
              flexShrink: 0,
              px: { xs: 2.5, md: 3 },
              display: "flex",
              alignItems: "center",
              gap: 1.5
            }}
          >
            {hasNav && (
              <IconButton aria-label={drawerOpen ? "Hide the navigation" : "Show the navigation"} onClick={() => setDrawerOpen((prev) => !prev)} sx={{ color: "text.primary" }} data-testid="nav-toggle">
                {drawerOpen ? <MenuOpenIcon /> : <MenuIcon />}
              </IconButton>
            )}
            <Box
              component="a"
              href="/"
              onClick={(event: React.MouseEvent) => {
                event.preventDefault();
                navigate("/");
              }}
              sx={{ display: "inline-flex", alignItems: "center", lineHeight: 0, flexShrink: 0 }}
            >
              <Box
                component="img"
                src="/bkdp/logos/BKDP_Logo.svg"
                alt="Blair Koch Digital Partners"
                sx={{ height: { xs: 36, md: 44 }, width: "auto", display: "block" }}
              />
            </Box>
          </Box>
          <Box sx={{ ml: { xs: 0, md: showDrawer && drawerOpen ? `${drawerWidth}px` : 0 }, flex: 1, minWidth: 0, display: "flex", transition: "margin-left 225ms cubic-bezier(0, 0, 0.2, 1)" }}>
            <Box
              sx={{
                width: "100%",
                boxSizing: "border-box",
                // The measure: main's maxWidth 1200 centred with 48px gutters, written as padding so the left gutter
                // can also clear zone 1 when the collapsed drawer leaves the frame over the content's left edge.
                pl: { xs: 0, md: `max(calc((100% - 1200px) / 2 + 48px), ${showDrawer && !drawerOpen ? drawerWidth : 48}px)` },
                pr: { xs: 2.5, md: "max(calc((100% - 1200px) / 2 + 48px), 48px)" },
                transition: "padding-left 225ms cubic-bezier(0, 0, 0.2, 1)",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 0
              }}
            >
              {/* ACT-3: above 900px the client's mark lives at the top of the drawer and this zone is empty; below
                  900px there is no drawer, so the mark stays here. The rule that separated two logos is gone. */}
              <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", minWidth: 0, px: 2 }}>
                {client && inClient && isMobile && (
                  client.logoUrl ? (
                    <Box
                      component="img"
                      src={client.logoUrl}
                      alt={client.name}
                      sx={{ height: 32, width: "auto", maxWidth: "100%", minWidth: 0, objectFit: "contain", objectPosition: "left center", display: "block" }}
                    />
                  ) : (
                    <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                      {client.name}
                    </Typography>
                  )
                )}
              </Box>
              {showGear && (
                <Tooltip title="Client settings">
                  <IconButton
                    aria-label="Client settings"
                    aria-current={inSettings ? "page" : undefined}
                    onClick={() => navigate(settingsHome!)}
                    sx={{
                      color: inSettings ? "secondary.main" : "text.primary",
                      // RDN-1: no background in any state; the selected state is a 2px bar beneath the icon, in
                      // secondary.main, present only under /c/:slug/settings. Hover and focus are the theme's defaults,
                      // the same as the person icon beside it, and aria-current carries the state for assistive technology.
                      position: "relative",
                      "&::after": inSettings ? { content: '""', position: "absolute", left: 10, right: 10, bottom: 2, height: 2, borderRadius: 1, bgcolor: "secondary.main" } : undefined
                    }}
                  >
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
              )}
              {/* TOP-2: "Viewing as BKDP" lives with the identity it describes. The caption sits beneath the person
                  icon inside the bar's 64px, outside the button's 40px hit area, so neither the bar nor the gear moves.
                  It is a word, not a chip or a dot, and it is not a control: the button's label carries the state. */}
              <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Tooltip title={viewingAsBkdp ? viewingSentence : ""}>
                  <IconButton aria-label={viewingAsBkdp ? `${accountLabel}. ${viewingSentence}` : accountLabel} onClick={(event) => setUserAnchorEl(event.currentTarget)} sx={{ color: "text.primary" }}>
                    <PersonIcon />
                  </IconButton>
                </Tooltip>
                {viewingAsBkdp && !isMobile && (
                  <Typography
                    component="span"
                    variant="overline"
                    aria-hidden
                    sx={{ position: "absolute", top: "100%", left: 0, right: 0, mt: "-2px", textAlign: "center", lineHeight: 1, letterSpacing: "2px", color: "secondary.main", whiteSpace: "nowrap", pointerEvents: "none" }}
                  >
                    BKDP
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={userAnchorEl}
        open={Boolean(userAnchorEl)}
        onClose={() => setUserAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { maxHeight: "calc(100vh - 88px)" } } }}
      >
        <Box sx={{ px: 2.5, py: 2, minWidth: 240 }}>
          <Typography variant="overline" sx={{ color: "secondary.main", display: "block", mb: 0.5 }}>
            Account
          </Typography>
          <Typography variant="subtitle2" sx={{ color: "text.primary" }}>
            {me?.user.name || "Signed-in user"}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            {me?.user.email || "—"}
          </Typography>
          {roleLabel && (
            <Typography variant="caption" sx={{ color: "text.disabled", display: "block", mt: 1 }}>
              {roleLabel}
            </Typography>
          )}
        </Box>
        {showAdminConsole && <Divider />}
        {showAdminConsole && <MenuItem onClick={() => goTo("/admin")}>Admin console</MenuItem>}
        {menuHasClients && <Divider />}
        {showSwitch && (
          <Typography variant="overline" sx={{ color: "secondary.main", display: "block", px: 2, pt: 1, pb: 0.5 }} component="div" role="presentation">
            Switch client
          </Typography>
        )}
        {showSwitch && activeMemberships.map((m) => (
          <MenuItem key={m.client.id} selected={m.client.id === client?.id} onClick={() => goTo(`/c/${m.client.slug}`)}>
            {m.client.name}
          </MenuItem>
        ))}
        {showAllClients && <MenuItem onClick={() => goTo("/select")}>All clients…</MenuItem>}
        <Divider />
        <MenuItem onClick={() => void handleLogout()}>Sign out</MenuItem>
      </Menu>

      {showDrawer && (
        <Drawer
          variant="persistent"
          open={drawerOpen}
          sx={{
            width: drawerOpen ? drawerWidth : 0,
            flexShrink: 0,
            overflowX: "hidden",
            "& .MuiDrawer-paper": {
              width: drawerOpen ? drawerWidth : 0,
              boxSizing: "border-box",
              overflowX: "hidden"
            }
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: { xs: 2.5, md: 6 },
          py: { xs: 4, md: 6 },
          mt: 9,
          maxWidth: 1200,
          width: "100%",
          mx: "auto",
          minWidth: 0
        }}
      >
        {children}
      </Box>

      {isMobile && hasNav && (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: false }}
          sx={{ "& .MuiDrawer-paper": { width: drawerWidth, maxWidth: "85vw", boxSizing: "border-box" } }}
          data-testid="nav-drawer"
        >
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
};
